import React, { useEffect } from 'react';
import styled from 'styled-components';
import { MdInfoOutline } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';
import { Colors, Tooltip, Typography } from '@inplayer-org/inplayer-ui';
import { transparentize } from 'polished';

// selectors
import { getNewSubscriptions } from 'ducks/merchantDucks/subscriptions/selectors';

// components
import { CustomCard } from 'components/CustomCard';
import { Title } from 'pages/MerchantPages/Analytics/components/shared';
import RootState from 'ducks/RootState';
import { fetchSubscriptionsStatus } from 'ducks/merchantDucks/subscriptions/actions';

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 2fr 1fr 1fr 1fr;
  height: 32.8rem;
  width: 100%;
`;

const StyledCustomCard = styled(CustomCard)`
  box-shadow: 0 0 4px 1px ${transparentize(0.87, Colors.fontDarkGray)};
  border-radius: 5px;
  border: none;
`;

const BaseFlexContainer = styled.div<{ hideBorderBottom?: boolean; showBorderRight?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  color: ${Colors.fontGray};
  padding: 1rem;
  border-bottom: ${({ hideBorderBottom }) => !hideBorderBottom && `1px solid ${Colors.gray}`};
  border-right: ${({ showBorderRight }) => showBorderRight && `1px solid ${Colors.gray}`};
`;

const FlexContainer = styled(BaseFlexContainer)`
  grid-column: 1 / span 2;
`;
interface IconProps {
  size: string;
  padding?: string;
}
const StyledIcon = styled(MdInfoOutline)<IconProps>`
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  padding-left: ${({ padding }) => padding};
`;

const SubscriptionsTotal = styled.span`
  color: ${Colors.skyBlue};
  font-size: ${({ theme }) => theme.font.sizes.extraLarge};
  font-weight: ${({ theme }) => theme.font.weights.bold};
`;

const Value = styled.span<{ color?: string; isBold?: boolean; positionRight?: boolean }>`
  color: ${({ color }) => color};
  font-size: ${({ theme }) => theme.font.sizes.large};
  font-weight: ${({ isBold, theme }) => isBold && theme.font.weights.bold};
  align-self: ${({ positionRight }) => positionRight && 'flex-end'};
`;

const ChurnInfo = styled.span<{ color?: string }>`
  color: ${({ color }) => color};
  font-size: ${({ theme }) => theme.font.sizes.normal};
  font-weight: bold;
`;

const TypographyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  margin: 0;
  padding: 0;

  > p {
    font-size: ${({ theme }) => theme.font.sizes.small};
    color: ${Colors.white};
  }
`;

const SubscriptionsInfoTile = () => {
  const { startDate, endDate } = useSelector((state: RootState) => state.datepicker);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchSubscriptionsStatus(startDate, endDate));
  }, []);
  const newSubscriptions = useSelector(getNewSubscriptions);
  const scaPaymentLink = 'https://inplayer.com/docs/payment-processing/credit-card/#sca';

  const subscriptionDetails = useSelector(
    (state: RootState) => state.subscriptions.totalSubscriptionsStatuses.statuses
  );
  const {
    total_active: activeSubs,
    total_trialing: trialingSubs,
    total_incomplete: incompleteSubs,
    total_expired: expiredSubs,
    total_ended: endedSubs,
    total_canceled: canceledSubs,
  } = subscriptionDetails;

  return (
    <StyledCustomCard direction="row" margin="0 1rem 0 0" width="30%" height="100%">
      <GridContainer>
        <FlexContainer>
          <div>
            <Title variant="p">
              <span>SUBSCRIPTIONS</span>
            </Title>
            <span>Total number of active subscriptions.</span>
            <Tooltip
              placement="right"
              content="Total number of active subscriptions is the number of customers whose subscription status is Recurrent."
            >
              <StyledIcon size="1rem" padding="3px" />
            </Tooltip>
          </div>
          <SubscriptionsTotal>{activeSubs}</SubscriptionsTotal>
        </FlexContainer>
        <BaseFlexContainer showBorderRight>
          <div>
            <Value isBold color={Colors.green}>
              NEW
            </Value>
            <Tooltip
              placement="right"
              content={
                <TypographyWrapper>
                  <Typography variant="p">
                    Customers who have started their subscription in the selected time frame and have completed their
                    first payment.
                  </Typography>
                  <Typography variant="p">This does not include Trialing subscribers.</Typography>
                </TypographyWrapper>
              }
            >
              <StyledIcon size="1rem" padding="3px" />
            </Tooltip>
          </div>

          <Value isBold color={Colors.darkGray}>
            {newSubscriptions}
          </Value>
        </BaseFlexContainer>
        <BaseFlexContainer>
          <div>
            <Value isBold color={Colors.yellow}>
              TRIALING
            </Value>
            <Tooltip placement="right" content="Customers who are still in their free trial period.">
              <StyledIcon size="1rem" padding="3px" />
            </Tooltip>
          </div>
          <Value isBold color={Colors.darkGray}>
            {trialingSubs}
          </Value>
        </BaseFlexContainer>
        <BaseFlexContainer showBorderRight>
          <div>
            <Value isBold color={Colors.navy}>
              CANCELED
            </Value>
            <Tooltip
              placement="right"
              content={
                <TypographyWrapper>
                  <Typography variant="p">Customers who have canceled their subscription to your service.</Typography>
                  <Typography variant="p">
                    These customers may still have access to your service until their access period expires.
                  </Typography>
                  <Typography variant="p">
                    The ones whose access is about to expire will be counted as about to churn.
                  </Typography>
                </TypographyWrapper>
              }
            >
              <StyledIcon size="1rem" padding="3px" />
            </Tooltip>
          </div>
          <Value isBold color={Colors.darkGray}>
            {canceledSubs}
          </Value>
          {/* {churnSubs > 0 && (
            <ChurnInfo color={Colors.darkGray}>of which {formatNumber(churnSubs)} about to churn</ChurnInfo>
          )} */}
        </BaseFlexContainer>
        <BaseFlexContainer>
          <div>
            <Value isBold color={Colors.red}>
              ENDED
            </Value>
            <Tooltip
              placement="right"
              content={
                <TypographyWrapper>
                  <Typography variant="p">
                    Customers whose subscription has expired and has not renewed due to an issue with their payment
                    method.
                  </Typography>
                  <Typography variant="p">These subscribers have also lost access to your service.</Typography>
                </TypographyWrapper>
              }
            >
              <StyledIcon size="1rem" padding="3px" />
            </Tooltip>
          </div>
          <Value isBold color={Colors.darkGray}>
            {endedSubs}
          </Value>
        </BaseFlexContainer>

        <BaseFlexContainer showBorderRight hideBorderBottom>
          <div>
            <Value isBold color={Colors.darkGray}>
              INCOMPLETE
            </Value>
            <Tooltip
              tag={`incomplete_${scaPaymentLink.toLowerCase()}`}
              placement="right"
              content={
                <TypographyWrapper>
                  <Typography variant="p">
                    Subscriptions for which the
                    <a href={scaPaymentLink}> SCA process </a>
                    has been initiated but not confirmed by the customer.
                  </Typography>
                  <Typography variant="p">
                    Or for non-EU customers, there was an issue with the payment method.
                  </Typography>
                </TypographyWrapper>
              }
            >
              <StyledIcon size="1rem" padding="3px" />
            </Tooltip>
          </div>
          <Value isBold color={Colors.darkGray}>
            {incompleteSubs}
          </Value>
        </BaseFlexContainer>
        <BaseFlexContainer hideBorderBottom>
          <div>
            <Value isBold color={Colors.darkGray}>
              EXPIRED
            </Value>
            <Tooltip
              tag={`expired_${scaPaymentLink.toLowerCase()}`}
              placement="right"
              content={
                <TypographyWrapper>
                  <Typography variant="p">
                    Subscriptions for which the
                    <a href={scaPaymentLink}> SCA process </a>
                    has expired. They cannot be re-activated.
                  </Typography>
                  <Typography variant="p">
                    Or for non-EU customers, there was an issue with the payment method.
                  </Typography>
                </TypographyWrapper>
              }
            >
              <StyledIcon size="1rem" padding="3px" />
            </Tooltip>
          </div>
          <Value isBold color={Colors.darkGray}>
            {expiredSubs}
          </Value>
        </BaseFlexContainer>
      </GridContainer>
    </StyledCustomCard>
  );
};

export default SubscriptionsInfoTile;
